import { ChevronLeft, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useCurrentPlayer } from '../guestPlayer';

/**
 * The board header every game kind shares (WS-17c): back, title, one status line, and a
 * slot for the kind's own controls (replay entry buttons, a resign button). Extracted when
 * Battleship arrived rather than duplicated — a spectator badge that says "Spectating" on
 * one board and nothing on another would be exactly the kind of drift a shared shell exists
 * to prevent.
 *
 * `status` is always a **fact** ("Your shot", "Faye won", "waiting for the seas to answer"),
 * never an instruction and never a count of how long you've left it
 * (`memory/no-demand-mechanics.md`).
 */
export function GameHeader({
  title,
  status,
  spectating,
  myTurn = false,
  finished = false,
  children,
}: {
  title: string;
  status: string;
  spectating: boolean;
  myTurn?: boolean;
  finished?: boolean;
  children?: ReactNode;
}): JSX.Element {
  const navigate = useNavigate();
  // A guest has no `/games` (it's behind the household login); their way back is the
  // friend-of-the-family lounge.
  const isGuest = useCurrentPlayer()?.isGuest ?? false;
  return (
    <header className="flex items-center gap-2">
      <button
        type="button"
        aria-label={isGuest ? 'Back to the lounge' : 'Back to Games'}
        className="flex h-11 w-11 items-center justify-center text-fg-3"
        onClick={() => navigate(isGuest ? '/play/lounge' : '/games')}
      >
        <ChevronLeft size={22} />
      </button>
      <div className="min-w-0 flex-1">
        <h2 className="truncate font-display text-xl tracking-tight text-fg">{title}</h2>
        <p
          className="text-sm text-fg-2"
          data-testid="game-status-line"
          data-my-turn={myTurn}
          data-finished={finished}
        >
          {spectating && (
            <span className="mr-1 inline-flex items-center gap-1 text-fg-3">
              <Eye size={13} aria-hidden="true" /> Spectating
            </span>
          )}
          {status}
        </p>
      </div>
      {children}
    </header>
  );
}
